import { useState } from 'react'
import type { RequestTypeVersionListItemDTO } from '@kfolks/types'
import { Modal } from '@/components/Modal'
import { Button } from '@/shared/components/atoms/ui/button'

/**
 * Confirmation dialog for the Restore action on a History row (task 9.5).
 * Captures an optional `changeNote` (the backend prepends "Restored from
 * v{N}: " automatically) and calls back when the admin confirms.
 *
 * Submission state (in-flight, validator errors) is owned by the parent
 * panel — this dialog just collects the note and dispatches `onConfirm`.
 * Disable both Cancel and Confirm while `isSubmitting` is true so the
 * admin can't double-fire the POST.
 */

export interface VersionRestoreDialogProps {
	open: boolean
	onClose: () => void
	version: RequestTypeVersionListItemDTO | null
	onConfirm: (input: { changeNote: string | null }) => void
	isSubmitting: boolean
	errorMessage: string | null
}

export function VersionRestoreDialog({
	open,
	onClose,
	version,
	onConfirm,
	isSubmitting,
	errorMessage,
}: VersionRestoreDialogProps) {
	const [changeNote, setChangeNote] = useState('')

	// Reset on close so re-opening doesn't show the previous note.
	const handleClose = () => {
		if (isSubmitting) return
		setChangeNote('')
		onClose()
	}

	if (!version) return null

	return (
		<Modal open={open} onClose={handleClose} title={`Restore v${version.versionNumber}?`} maxWidth="md">
			<div className="space-y-4">
				<p className="text-sm text-muted-foreground">
					This appends a new version on top of the current history with the v{version.versionNumber} payload as the new
					current. Historical rows are never mutated. The backend re-validates the payload against the current world
					(data sources, summary-template field ids, workflow graph) and rejects with 422 if it's stale.
				</p>

				<label className="block space-y-1 text-sm">
					<span className="font-medium">Change note (optional)</span>
					<textarea
						value={changeNote}
						onChange={(e) => setChangeNote(e.target.value)}
						disabled={isSubmitting}
						maxLength={500}
						rows={3}
						className="w-full rounded-md border bg-background p-2 text-sm"
						placeholder="Why are you restoring this version?"
						aria-label="Change note"
					/>
					<span className="text-xs text-muted-foreground">
						The backend prepends "Restored from v{version.versionNumber}: " to whatever you write here.
					</span>
				</label>

				{errorMessage ? (
					<div
						role="alert"
						className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
					>
						{errorMessage}
					</div>
				) : null}

				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button
						onClick={() => onConfirm({ changeNote: changeNote.trim() === '' ? null : changeNote.trim() })}
						disabled={isSubmitting}
						aria-label={`Confirm restore version ${version.versionNumber}`}
					>
						{isSubmitting ? 'Restoring…' : 'Restore'}
					</Button>
				</div>
			</div>
		</Modal>
	)
}
